import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import {
  EDUCATION_LEVEL_LABELS,
  type PersonnelDetail,
  type PersonnelEducation,
} from '../personnelTypes';

/**
 * Educational background — CS Form 212, section III.
 *
 * **A table, not a card per line**, for the reason the service record is one: this is read
 * by scanning down a column — where they studied, what they took, whether they finished — and
 * a stack of label/value pairs makes that a scroll. It owes the standard contract either way:
 * search, sort, resize, page.
 *
 * In its own file rather than inside `PersonnelProfilePage`, where the other panels live. That
 * page is past 1,300 lines and is being edited in another session; a new panel there would be
 * three sets of hands in one file for no benefit the reader gets.
 */
export function EducationPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (education: PersonnelEducation) => void;
  onDelete: (education: PersonnelEducation) => void;
  onBulkDelete: (educations: PersonnelEducation[]) => void;
  onRestore: (education: PersonnelEducation) => void;
}) {
  /*
    Create in the toolbar's leading slot — the same position, variant and icon the index page
    uses, because adding a line is adding a record.

    Hidden without the key, never disabled: a pure permission gate hides. Disabled here is for
    STATE — a deleted employee's 201 file is not edited — and the tooltip says why rather than
    leaving a dead button.
  */

  /*
    Deleted rows are hidden at rest and the cog lifts them — the arrangement every list in the
    product has. NOT persisted: a view that quietly kept showing deleted rows from yesterday is
    how a deleted record gets treated as live.
  */
  const [showDeleted, setShowDeleted] = useState(false);

  const educations = useMemo(
    () => (showDeleted ? record.educations : record.educations.filter((e) => e.isActive)),
    [record.educations, showDeleted],
  );

  /*
    Searched, sorted and paged IN MEMORY, which is correct here for the same reason it is on
    the service record: the rule against client-side filtering is about a paged list whose
    filter silently applies to the rows on screen and not the hundreds behind them. These
    arrive whole with the record — one endpoint, every row — so the filter sees everything.
  */
  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? educations.filter((e) =>
            [
              e.schoolName,
              e.degreeOrCourse,
              e.honors,
              e.highestLevelUnitsEarned,
              EDUCATION_LEVEL_LABELS[e.level],
            ]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : educations;

      const field = query.sort?.replace(/^-/, '') ?? 'level';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'level') {
          /*
            The enum IS the order a 201 file is read in, so compare the NUMBERS rather than the
            labels — alphabetically "College" sorts before "Elementary", which would print a
            degree above the primary school that preceded it.
          */
          const byLevel = a.level - b.level || a.periodFrom.localeCompare(b.periodFrom);
          return byLevel * (descending ? -1 : 1);
        }

        // No schoolName branch: the school has no column of its own to sort by since it
        // joined the anchor, and a comparison nothing can reach is dead code.
        const pick = (row: PersonnelEducation) =>
          field === 'degreeOrCourse'
            ? (row.degreeOrCourse ?? '')
            : field === 'yearGraduated'
              ? String(row.yearGraduated ?? '')
              : row.periodFrom;

        return String(pick(a)).localeCompare(String(pick(b))) * (descending ? -1 : 1);
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [educations],
  );

  const state = useDataTable<PersonnelEducation>({
    fetchPage,
    pageSize: 10,

    // Earliest level first: education is read upward, not by recency.
    defaultSort: 'level',
  });

  const columns: DataColumn<PersonnelEducation>[] = [
    {
      /*
        THE ANCHOR, and it carries both values because either one alone is a title that does
        not identify the row. "College" titles every degree a reader holds identically, and
        the school alone does not say which of three enrolments it was — the list view titled
        every row with the level and buried the school in the summary line, which is the same
        call the family panel's Name column records, one step along.

        Two values in one cell, which the one-column-one-value rule otherwise refuses — and
        what that rule protects against is what makes this the exception rather than a breach
        of it. It guards a value the machinery cannot see: no header, no width entry, no sort,
        and a row whose height stops matching its neighbours. This is one line, under one
        header, at one width, and nothing is stacked. The cost is the sort, and it is real:
        the school no longer has a column to order by. On a 201 file's handful of lines that
        was never the useful order — the level is, and it is the default — and the school is
        still searchable.
      */
      key: 'level',
      header: 'Level and school',
      width: 340,
      sortable: true,
      render: (e) => <TextCell value={`${EDUCATION_LEVEL_LABELS[e.level]} — ${e.schoolName}`} />,
    },
    {
      key: 'degreeOrCourse',
      header: 'Degree or course',
      width: 220,
      sortable: true,
      /*
        The summary line is NOMINATED rather than left to the fallback, and the school joining
        the title is what forced it: the fallback takes the first three columns after the
        anchor, which now reaches `periodTo` — blank for study still in progress, so those rows
        spent a summary slot rendering an em dash. What identifies a line of education is what
        was taken, when it started, and whether it finished.
      */
      summary: true,
      // The form does not ask for one at elementary or secondary level, so a blank here is a
      // question that was never put rather than an answer that went missing.
      render: (e) => <TextCell value={e.degreeOrCourse} />,
    },
    {
      // Calendar dates, so the conversion-free formatter: a period of attendance names days,
      // and the instant path would shift them for a reader west of UTC.
      key: 'periodFrom',
      header: 'From',
      width: 130,
      sortable: true,
      summary: true,
      render: (e) => <TextCell value={formatDateOnly(e.periodFrom)} mono />,
    },
    {
      key: 'periodTo',
      header: 'To',
      width: 130,
      // Blank, not an em dash: study still in progress has no end date rather than a missing one.
      render: (e) => <TextCell value={e.periodTo ? formatDateOnly(e.periodTo) : null} mono />,
    },
    {
      key: 'yearGraduated',
      header: 'Graduated',
      width: 120,
      sortable: true,
      summary: true,
      /*
        A YEAR, so it goes through no date formatter at all — and `numeric` would be wrong for
        it: that dims a zero, and a year is an identifier rather than a quantity. Mono, like
        every other machine value.
      */
      render: (e) => <TextCell value={e.yearGraduated ? String(e.yearGraduated) : null} mono />,
    },
    {
      key: 'highestLevelUnitsEarned',
      header: 'Highest level / units',
      width: 170,
      render: (e) => <TextCell value={e.highestLevelUnitsEarned} />,
    },
    {
      key: 'honors',
      header: 'Honours',
      width: 200,
      render: (e) => <TextCell value={e.honors} />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row
        carries no marker while Show deleted is on, which is the one state a row must
        never be silent about. This panel offered Show deleted and had no column to show
        it in. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelEducation>
        id="personnel-education"
        state={state}
        /*
          The first column opens the row as a key/value preview. A table row compares records;
          it is a poor way to read ONE, because the columns are spread across a scroll and the
          narrow ones truncate. The preview is built from these same `columns`, so it cannot
          drift from the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Education', size: '2xl' }}
        columns={columns}
        rowKey={(e) => e.id}
        searchPlaceholder="Search education"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          The kebab's entries. The shell owns the gutter, the menu and the right-click
          shortcut — this is only what goes IN the menu, and the same array feeds the table
          and the list, so the two cannot offer different actions on one row.

          Delete and Restore are the two halves of one state, so only the applicable one is
          offered. That is not the disable-for-state case: "restore a live row" is not
          something the reader is part-way through, it is simply not an action here.
        */
        rowActions={(e) => [
          {
            key: `edit-${e.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: e.isActive
              ? null
              : 'This line is deleted. Restore it before editing.',
            onClick: () => onEdit(e),
          },
          e.isActive
            ? {
                key: `delete-${e.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(e),
              }
            : {
                key: `restore-${e.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(e),
              },
        ]}
        /*
          The trail the columns do not carry — the updater, and the ids. Supplying it also
          earns the Table/List switch on the cog, which the panel offers only when it has
          provenance to lead a list row with.
        */
        otherInfo={(e) => ({
          createdAt: e.createdAt,
          createdByName: e.createdByName,
          updatedAt: e.updatedAt,
          updatedByName: e.updatedByName,
        })}
        /*
          The panel already drew the checkbox and had nothing behind it — the gap CLAUDE.md
          calls out: a panel that gets the column should grow a bulk action.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((e) => e.isActive);

          return [
            {
              key: 'delete-educations',
              label: `Delete ${deletable.length} education line${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected education line is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No educational background recorded yet."
        emptySearchMessage="No education line matches that search."
      />
    </ProfileSection>
  );
}
